import * as THREE from 'three'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js'

class ModelLoaderService {
    constructor() {
        this.loader = null
        this.cache = new Map()
    }

    getLoader() {
        if (!this.loader) {
            this.loader = new GLTFLoader()
            this.loader.setMeshoptDecoder(MeshoptDecoder)
        }
        return this.loader
    }

    async load(url, { castShadow = true, receiveShadow = true } = {}) {
        if (!this.cache.has(url)) {
            this.cache.set(url, this.getLoader().loadAsync(url))
        }
        const gltf = await this.cache.get(url)
        const scene = gltf.scene.clone(true)
        scene.traverse((object) => {
            if (object.isMesh) {
                object.castShadow = castShadow
                object.receiveShadow = receiveShadow
                object.frustumCulled = true
            }
        })
        return scene
    }

    normalize(object, targetHeight = 1) {
        const box = new THREE.Box3().setFromObject(object)
        const size = new THREE.Vector3()
        const center = new THREE.Vector3()
        box.getSize(size)
        box.getCenter(center)

        const scale = size.y > 0 ? targetHeight / size.y : 1
        object.scale.setScalar(scale)
        object.position.set(-center.x * scale, -box.min.y * scale, -center.z * scale)
        return object
    }

    async loadToken(url, targetHeight = 1) {
        return this.normalize(await this.load(url), targetHeight)
    }

    /**
     * Seat a model on the floor, sized by the ground it covers rather than by how
     * tall it is.
     *
     * Scenery that stands on a grid is placed by its FOOTPRINT: a plinth has to be
     * as wide as the cell it belongs to, and how tall that makes it is the model's
     * own business. Sizing it by height instead lets the proportions of whatever
     * came out of the generator decide how much of the board it covers, which is how
     * a waist-high plinth ends up swallowing its neighbours.
     *
     * The result is returned wrapped in a holder, because the seating lives in the
     * model's own position and scale — a caller that then sets those to place the
     * object on the board would silently undo it. Move the holder instead.
     */
    seat(object, targetFootprint = 1) {
        const box = new THREE.Box3().setFromObject(object)
        const size = new THREE.Vector3()
        const center = new THREE.Vector3()
        box.getSize(size)
        box.getCenter(center)

        const spread = Math.max(size.x, size.z)
        const scale = spread > 0 ? targetFootprint / spread : 1
        object.scale.setScalar(scale)
        object.position.set(-center.x * scale, -box.min.y * scale, -center.z * scale)

        const holder = new THREE.Group()
        holder.add(object)
        holder.userData.height = size.y * scale
        holder.userData.footprint = targetFootprint
        return holder
    }

    async loadProp(url, targetFootprint = 1) {
        return this.seat(await this.load(url), targetFootprint)
    }

    dispose(url) {
        this.cache.delete(url)
    }

    clearCache() {
        this.cache.clear()
    }
}

export default new ModelLoaderService()
